import React, { useCallback, useEffect, useRef, useState } from 'react'
import {
  SvgSuccessToast,
  SvgCross,
  SvgErrorToast,
  SvgInfoToast,
} from '../assets/images'
import { resolveToastDurationMs, ToastDuration } from '../utils/constants'

interface ToastProps {
  message: string
  type: 'success' | 'error' | 'info' | 'warning'
  onClose: () => void
  duration?: ToastDuration
}

const EXIT_ANIMATION_MS = 500

const Toast: React.FC<ToastProps> = ({ message, type, onClose, duration }) => {
  const dismissMs = resolveToastDurationMs(duration)
  const [isVisible, setIsVisible] = useState<boolean>(false)
  const dismissTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const onCloseRef = useRef(onClose)

  onCloseRef.current = onClose

  const clearTimers = useCallback(() => {
    if (dismissTimerRef.current) {
      clearTimeout(dismissTimerRef.current)
      dismissTimerRef.current = null
    }
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current)
      closeTimerRef.current = null
    }
  }, [])

  const dismissToast = useCallback(() => {
    clearTimers()
    setIsVisible(false)
    closeTimerRef.current = setTimeout(() => {
      onCloseRef.current()
    }, EXIT_ANIMATION_MS)
  }, [clearTimers])

  useEffect(() => {
    setIsVisible(true)

    dismissTimerRef.current = setTimeout(() => {
      dismissToast()
    }, dismissMs)

    return () => clearTimers()
  }, [clearTimers, dismissToast, dismissMs])

  const toastClasses = `flex p-4 gap-4 border-2 rounded-lg shadow-md sm:!w-[25rem] w-[18rem] transition-all duration-500 transform items-center ${
    isVisible
      ? '-translate-x-6 pointer-events-auto'
      : 'translate-x-full pointer-events-none'
  }`

  return (
    <div
      className={`${toastClasses} ${
        type === 'success'
          ? 'bg-success-25 border-success-300'
          : type === 'info'
          ? 'bg-yellow-50 border-yellow-300'
          : 'bg-red-50 border-error-300'
      }`}
    >
      <div
        className={`p-2 rounded-md ${
          type === 'success'
            ? 'bg-success-700'
            : type === 'info'
            ? 'bg-yellow-500'
            : 'bg-error-400'
        }`}
      >
        {type === 'success' ? (
          <SvgSuccessToast />
        ) : type === 'info' ? (
          <SvgInfoToast />
        ) : (
          <SvgErrorToast />
        )}
      </div>
      <div className="flex w-full justify-between items-center">
        <div className="font-primary text-xs font-light">{message}</div>
        <div className="cursor-pointer" onClick={dismissToast}>
          <SvgCross />
        </div>
      </div>
    </div>
  )
}

export default Toast
